import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Grid, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from 'recharts';
import {
  DISCLAIMER, TIER_LABEL, investmentPlanService,
  type HoldingRisk, type RiskProfile, type RiskTier,
} from '../services/investment-plan-service';
import { baseMoney, useBaseCurrency } from '../stores/currency-store';

const TIER_COLOR: Record<RiskTier, string> = {
  core_index: '#2e9e6b',
  blue_chip: '#3b82c4',
  income: '#8a7fd0',
  growth: '#d39b2a',
  speculative: '#d0533f',
};

const TREND: Record<string, { label: string; color: string }> = {
  uptrend: { label: 'Uptrend', color: 'var(--inflow-text)' },
  pullback: { label: 'Pullback', color: 'var(--text-secondary)' },
  recovering: { label: 'Recovering', color: 'var(--info-text)' },
  downtrend: { label: 'Downtrend', color: 'var(--warn-text)' },
  falling_knife: { label: 'Falling knife', color: 'var(--danger-text)' },
  unknown: { label: '—', color: 'var(--text-secondary)' },
};

const LABEL_COLOR: Record<string, string> = {
  conservative: 'var(--info-text)', moderate: 'var(--info-text)', balanced: 'var(--inflow-text)',
  growth: 'var(--warn-text)', aggressive: 'var(--danger-text)',
};

const num = (v?: number | null, digits = 0, suffix = '') =>
  v === undefined || v === null ? '—' : `${v.toFixed(digits)}${suffix}`;

/** "+12.3" / "-4.0" */
const signed = (v?: number | null) => (v === undefined || v === null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}`);

/**
 * Investments → Risk (issue #33): the equity sleeve by risk tier, the
 * portfolio's risk label against the plan's target, and a row per holding
 * with its tier (and why), volatility, beta, drawdown, trend and the latest
 * Jev verdict. Tiers can be overridden per symbol.
 */
export const RiskPage: React.FC = () => {
  useBaseCurrency();
  const [risk, setRisk] = useState<RiskProfile | null>(null);
  const [asOf, setAsOf] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overrides, setOverrides] = useState<{ symbol: string; tier: RiskTier; reason?: string }[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o] = await Promise.all([investmentPlanService.risk(), investmentPlanService.riskTiers()]);
      setRisk(r.risk ?? null);
      setAsOf(r.as_of ?? '');
      setOverrides(o.overrides);
      setError(r.configured ? null : 'Save an investment plan first.');
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load risk');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const setTier = async (h: HoldingRisk, tier: RiskTier | '') => {
    const rest = overrides.filter((o) => o.symbol.toUpperCase() !== h.symbol.toUpperCase());
    const next = tier ? [...rest, { symbol: h.symbol, tier, reason: 'set by the household' }] : rest;
    await investmentPlanService.setRiskTier(next);
    await load();
  };

  const pie = useMemo(() => (risk?.tiers ?? []).map((t) => ({ name: TIER_LABEL[t.tier], value: parseFloat(t.value), tier: t.tier })), [risk]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>Risk</Typography>
      <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 2 }}>
        Equity holdings by risk tier, priced {asOf || 'today'} from a year of daily closes.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>{DISCLAIMER}</Alert>
      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
      {risk && (
        <>
          {risk.alerts.map((a) => <Alert key={a} severity="warning" sx={{ mb: 1 }}>{a}</Alert>)}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }} component="section" aria-label="Risk profile">
                <CardContent>
                  <Typography variant="h6" component="h2">Risk profile</Typography>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: LABEL_COLOR[risk.label] }} data-testid="risk-label">
                    {risk.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                    Score {risk.score.toFixed(2)} · plan target {risk.target_label} ({risk.vs_target})
                  </Typography>
                  <Stack sx={{ mt: 2 }} gap={0.5}>
                    <Typography variant="body2">Equity {risk.equity_pct_of_investable}% of investable ({baseMoney(risk.equity_value, 0)})</Typography>
                    <Typography variant="body2">Weighted volatility {num(risk.weighted_vol_pct, 1, '%')}</Typography>
                    <Typography variant="body2">Top 10 single stocks {risk.top_single_stocks_pct_of_investable}% of investable</Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card sx={{ height: '100%' }} component="section" aria-label="Tiers">
                <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Box sx={{ width: 220, height: 220 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={pie} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} stroke="none">
                          {pie.map((p) => <Cell key={p.tier} fill={TIER_COLOR[p.tier]} />)}
                        </Pie>
                        <RTooltip formatter={(v: number) => baseMoney(v, 0)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 260 }}>
                    {risk.tiers.map((t) => (
                      <Box key={t.tier} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: TIER_COLOR[t.tier] }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>{TIER_LABEL[t.tier]} ({t.count})</Typography>
                        <Typography variant="body2" className="fw-num">
                          {t.pct_of_equity}% of equity · {t.pct_of_investable}% of investable
                          {t.cap_pct ? ` · cap ${t.cap_pct}%` : ''}
                        </Typography>
                        {t.over_cap && <Chip size="small" variant="outlined" label="over cap"
                          sx={{ color: 'var(--danger-text)', borderColor: 'var(--danger-text)' }} />}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TableContainer component={Card}>
            <Table size="small" aria-label="Holdings by risk">
              <TableHead>
                <TableRow>
                  <TableCell>Holding</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">% invest.</TableCell>
                  <TableCell align="right">Vol 1y</TableCell>
                  <TableCell align="right">Beta</TableCell>
                  <TableCell align="right">Max DD</TableCell>
                  <TableCell align="right">From high</TableCell>
                  <TableCell align="right">3m vs idx</TableCell>
                  <TableCell>Trend</TableCell>
                  <TableCell>Jev</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {risk.holdings.map((h) => {
                  const tr = TREND[h.trend] ?? TREND.unknown;
                  return (
                    <TableRow key={h.symbol} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 650 }}>{h.symbol}</Typography>
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>{h.fund ? 'fund' : 'stock'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`${h.tier_reason} (${h.tier_source})`}>
                          <select aria-label={`${h.symbol} tier`} value={h.tier_source === 'household' ? h.tier : ''}
                            onChange={(e) => void setTier(h, e.target.value as RiskTier | '')}
                            style={{ background: 'transparent', color: TIER_COLOR[h.tier], border: '1px solid var(--border-soft)',
                              borderRadius: 6, padding: '2px 4px', font: 'inherit' }}>
                            <option value="">{TIER_LABEL[h.tier]}{h.tier_source === 'household' ? '' : ' (auto)'}</option>
                            {(Object.keys(TIER_LABEL) as RiskTier[]).map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
                          </select>
                        </Tooltip>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--text-secondary)', maxWidth: 260 }}>
                          {h.tier_reason}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" className="fw-num">{baseMoney(h.value_base, 0)}</TableCell>
                      <TableCell align="right" className="fw-num">{h.pct_of_investable}%</TableCell>
                      <TableCell align="right" className="fw-num">{num(h.metrics.vol_pct, 0, '%')}</TableCell>
                      <TableCell align="right" className="fw-num">{num(h.metrics.beta, 2)}</TableCell>
                      <TableCell align="right" className="fw-num">{num(h.metrics.max_drawdown_pct, 0, '%')}</TableCell>
                      <TableCell align="right" className="fw-num">{num(h.metrics.from_high_pct, 0, '%')}</TableCell>
                      <TableCell align="right" className="fw-num">{signed(h.metrics.rel_3m_pct)}</TableCell>
                      <TableCell>
                        {h.fund ? <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>—</Typography> : (
                          <Tooltip title={h.trend_line ?? ''}>
                            <Typography variant="body2" sx={{ color: tr.color, fontWeight: 600 }}>{tr.label}</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {h.verdict ? (
                          <Chip size="small" variant="outlined"
                            label={`${h.verdict.choice.replace('_', ' ')} ${h.verdict.confidence.toFixed(2)}`}
                            sx={h.verdict.high_conviction ? { color: 'var(--danger-text)', borderColor: 'var(--danger-text)' } : {}} />
                        ) : <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>—</Typography>}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};
